import React from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { AlertTriangle } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const HBar = ({ label, amount, count, max, color }) => (
  <div>
    <div className="flex items-center justify-between text-xs mb-1">
      <span className="text-foreground font-medium">{label}</span>
      <span className="text-muted-foreground tabular-nums">
        {fmt(amount)}<span className="ml-2 text-[10px]">({count} paid)</span>
      </span>
    </div>
    <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${max > 0 ? (amount / max) * 100 : 0}%` }} />
    </div>
  </div>
);

export const PlanTypeBreakdown = ({ stats }) => {
  const plans = Object.entries(stats.plan_breakdown || {}).sort((a, b) => b[1].amount - a[1].amount);
  const planMax = Math.max(1, ...plans.map(([, d]) => d.amount));
  const types = [
    { key: "subscriptions", label: "Subscriptions", color: "bg-blue-500" },
    { key: "topups", label: "Balance Top-ups", color: "bg-emerald-500" },
    { key: "platform_fees", label: "Platform Fees (3.8% + $0.30)", color: "bg-cyan-500" },
  ].map((t) => ({ ...t, ...(stats.type_breakdown?.[t.key] || { count: 0, amount: 0 }) }));
  const typeMax = Math.max(1, ...types.map((t) => t.amount));
  const attempts = Object.entries(stats.attempts_breakdown || {});
  const planColors = { Premium: "bg-purple-500", Pro: "bg-blue-500", Standard: "bg-teal-500", "Multi-Location Starter": "bg-orange-500" };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="border-border bg-card" data-testid="plan-breakdown-card">
        <CardContent className="p-5">
          <p className="text-sm font-semibold text-foreground mb-1">Revenue by Plan</p>
          <p className="text-[11px] text-muted-foreground mb-3">Paid subscription payments only</p>
          <div className="space-y-3">
            {plans.map(([plan, d]) => (
              <HBar key={plan} label={plan} amount={d.amount} count={d.count} max={planMax} color={planColors[plan] || "bg-slate-400"} />
            ))}
            {plans.length === 0 && <p className="text-xs text-muted-foreground">No paid subscription payments in period</p>}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border bg-card" data-testid="type-breakdown-card">
        <CardContent className="p-5">
          <p className="text-sm font-semibold text-foreground mb-1">Fieldz Revenue by Type</p>
          <p className="text-[11px] text-muted-foreground mb-3">Paid only — reconciles with the top cards</p>
          <div className="space-y-3">
            {types.map((t) => (
              <HBar key={t.key} label={t.label} amount={t.amount} count={t.count} max={typeMax} color={t.color} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border bg-card" data-testid="attempts-breakdown-card">
        <CardContent className="p-5">
          <p className="text-sm font-semibold text-foreground mb-1">Payment Attempts (not revenue)</p>
          <p className="text-[11px] text-muted-foreground mb-3">Deduped · pending &gt;24h shown as expired</p>
          <div className="space-y-2">
            {attempts.map(([status, d]) => (
              <div key={status} className="flex items-center justify-between text-xs py-1.5 px-2 rounded bg-muted/30">
                <span className="text-foreground capitalize">{status}</span>
                <span className="text-muted-foreground tabular-nums">{d.count} · {fmt(d.amount)}</span>
              </div>
            ))}
            {attempts.length === 0 && <p className="text-xs text-muted-foreground">No non-paid attempts in period</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const MrrBreakdownCard = ({ stats }) => (
  <Card className="border-border bg-card" data-testid="mrr-breakdown-card">
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-foreground">MRR Breakdown</p>
          <p className="text-[11px] text-muted-foreground">One row per paying owner — verify by eye</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-foreground tabular-nums" data-testid="mrr-breakdown-total">{fmt(stats.mrr)}</p>
          <p className="text-[11px] text-muted-foreground">{stats.paying_customers} customers</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="mrr-breakdown-table">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left text-[10px] uppercase text-muted-foreground py-1.5 pr-3">Owner</th>
              <th className="text-left text-[10px] uppercase text-muted-foreground py-1.5 pr-3">Plan</th>
              <th className="text-left text-[10px] uppercase text-muted-foreground py-1.5 pr-3">Billing</th>
              <th className="text-right text-[10px] uppercase text-muted-foreground py-1.5">Monthly</th>
            </tr>
          </thead>
          <tbody>
            {(stats.mrr_breakdown || []).map((r) => (
              <tr key={r.user_id} className="border-b border-border/40 last:border-0">
                <td className="py-2 pr-3">
                  <p className="text-xs text-foreground truncate max-w-[220px]">{r.email}</p>
                  {r.company && <p className="text-[10px] text-muted-foreground truncate max-w-[220px]">{r.company}</p>}
                </td>
                <td className="py-2 pr-3"><Badge className="text-[10px] bg-blue-500/15 text-blue-400">{r.plan}</Badge>
                  {r.is_internal && <Badge className="ml-1 text-[10px] bg-amber-500/15 text-amber-400">Internal</Badge>}</td>
                <td className="py-2 pr-3 text-xs text-muted-foreground capitalize">{r.billing_period}</td>
                <td className="py-2 text-right text-xs font-semibold text-foreground tabular-nums">{fmt(r.monthly_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(stats.mrr_breakdown || []).length === 0 && (
          <p className="text-xs text-muted-foreground py-4 text-center">No paying customers</p>
        )}
      </div>
    </CardContent>
  </Card>
);

export const NeedsReviewCard = ({ stats }) => {
  const items = stats.needs_review || [];
  if (items.length === 0) return null;
  return (
    <Card className="border-amber-500/30 bg-amber-500/5" data-testid="needs-review-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <p className="text-sm font-semibold text-foreground">Needs Review — unmapped paid transactions ({fmt(stats.needs_review_total)})</p>
        </div>
        <div className="space-y-1.5">
          {items.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center justify-between gap-2 text-xs py-1.5 px-2 rounded bg-background/50 border border-border">
              <span className="font-mono text-[10px] text-muted-foreground">{r.transaction_id || r.session_id || r.payment_intent_id || "no-id"}</span>
              <span className="text-muted-foreground">{r.payment_type || "no type"}{r.reason ? ` — ${r.reason}` : ""}</span>
              <span className="font-semibold text-foreground tabular-nums">{fmt(r.amount)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const chartTheme = {
  grid: "rgba(148,163,184,0.12)",
  tick: { fontSize: 10, fill: "#94a3b8" },
  tooltip: { backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 11 },
};

const MiniChart = ({ title, data, dataKey, type = "bar", color, money, testId }) => (
  <Card className="border-border bg-card" data-testid={testId}>
    <CardContent className="p-4">
      <p className="text-xs font-semibold text-foreground mb-2">{title}</p>
      <ResponsiveContainer width="100%" height={150}>
        {type === "bar" ? (
          <BarChart data={data} margin={{ top: 4, right: 4, left: money ? 4 : -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
            <XAxis dataKey="month" tick={chartTheme.tick} tickLine={false} axisLine={false} />
            <YAxis tick={chartTheme.tick} tickLine={false} axisLine={false} width={money ? 50 : 28}
              tickFormatter={(v) => (money ? `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}` : v)} />
            <Tooltip contentStyle={chartTheme.tooltip} formatter={(v) => (money ? fmt(v) : v)} />
            <Bar dataKey={dataKey} fill={color} radius={[3, 3, 0, 0]} />
          </BarChart>
        ) : (
          <LineChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
            <XAxis dataKey="month" tick={chartTheme.tick} tickLine={false} axisLine={false} />
            <YAxis tick={chartTheme.tick} tickLine={false} axisLine={false} width={50}
              tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`} />
            <Tooltip contentStyle={chartTheme.tooltip} formatter={(v) => fmt(v)} />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
          </LineChart>
        )}
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

export const MonthlyCharts = ({ stats }) => {
  const m = stats.monthly || {};
  const data = (m.labels || []).map((label, i) => ({
    month: label.slice(2),
    new_paying: m.new_paying?.[i] || 0,
    churned: m.churned?.[i] || 0,
    mrr: m.mrr?.[i] || 0,
    volume: m.volume?.[i] || 0,
  }));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="monthly-charts">
      <MiniChart title="New Paying Customers" data={data} dataKey="new_paying" color="#10b981" testId="chart-new-paying" />
      <MiniChart title="Churned Customers" data={data} dataKey="churned" color="#ef4444" testId="chart-churned" />
      <MiniChart title="MRR (billed)" data={data} dataKey="mrr" type="line" color="#8b5cf6" money testId="chart-mrr" />
      <MiniChart title="Payment Volume Processed" data={data} dataKey="volume" color="#f59e0b" money testId="chart-volume" />
    </div>
  );
};
